import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Container, Row, Col, Card, Spinner, Alert, Badge, Table, Button,
} from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import {
  FaClock, FaTasks, FaFileInvoice, FaHandshake,
  FaCalendarAlt, FaWallet,
} from 'react-icons/fa';
import { api } from '../../contexts/AuthContext';

const ACCENT = '#60A5FA';

const euro = (n) =>
  `€${(Number(n) || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const inRange = (date, start, end) =>
  moment(date).isBetween(moment(start).startOf('day'), moment(end).endOf('day'), null, '[]');

const toMonthly = (amount, frequency) => {
  const n = Number(amount) || 0;
  switch (frequency) {
    case 'daily':
      return n * 30;
    case 'weekly':
      return n * (52 / 12);
    case 'monthly':
      return n;
    case 'quarterly':
      return n / 3;
    case 'yearly':
      return n / 12;
    default:
      return n;
  }
};

const StatCard = ({ icon, label, value, sub, color }) => (
  <Card className="bg-dark text-light border-secondary h-100">
    <Card.Body>
      <div className="d-flex align-items-center gap-2 mb-1" style={{ color: '#9ca3af', fontSize: 13 }}>
        <span style={{ color: color || ACCENT }}>{icon}</span>
        {label}
      </div>
      <h3 className="mb-1" style={{ color: color || '#fff' }}>{value}</h3>
      {sub ? <div style={{ color: '#9ca3af', fontSize: 12 }}>{sub}</div> : null}
    </Card.Body>
  </Card>
);

const Section = ({ title, action, children }) => (
  <Card className="bg-dark text-light border-secondary mb-3">
    <Card.Header className="bg-dark border-secondary d-flex justify-content-between align-items-center">
      <span style={{ color: ACCENT, fontWeight: 600 }}>{title}</span>
      {action}
    </Card.Header>
    <Card.Body>{children}</Card.Body>
  </Card>
);

const BarRow = ({ label, value, max, color }) => {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="mb-2">
      <div className="d-flex justify-content-between" style={{ fontSize: 13 }}>
        <span style={{ color: '#d1d5db' }}>{label}</span>
        <span style={{ color: color || '#fff', fontWeight: 600 }}>{euro(value)}</span>
      </div>
      <div style={{ height: 6, background: '#2d2d2d', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color || ACCENT }} />
      </div>
    </div>
  );
};

const Reporting = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState(moment().startOf('month').toDate());
  const [endDate, setEndDate] = useState(moment().endOf('month').toDate());

  const [transactions, setTransactions] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [debts, setDebts] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [wishlist, setWishlist] = useState([]);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [
        txRes, subRes, accRes, invRes, debtRes, taskRes, timeRes, wishRes,
      ] = await Promise.all([
        api.get('/api/transactions/'),
        api.get('/api/subscriptions/').catch(() => ({ data: [] })),
        api.get('/api/accounts/').catch(() => ({ data: [] })),
        api.get('/api/invoices/').catch(() => ({ data: [] })),
        api.get('/api/debts/').catch(() => ({ data: [] })),
        api.get('/api/tasks/').catch(() => ({ data: [] })),
        api.get('/api/time-entries/').catch(() => ({ data: [] })),
        api.get('/api/wishlists/').catch(() => ({ data: [] })),
      ]);
      setTransactions(txRes.data || []);
      setSubscriptions(subRes.data || []);
      setAccounts(accRes.data || []);
      setInvoices(invRes.data || []);
      setDebts(debtRes.data || []);
      setTasks(taskRes.data || []);
      setTimeEntries(timeRes.data || []);
      setWishlist(wishRes.data || []);
    } catch (err) {
      console.error(err);
      setError('Failed to load reporting data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const setPreset = (preset) => {
    if (preset === 'month') {
      setStartDate(moment().startOf('month').toDate());
      setEndDate(moment().endOf('month').toDate());
    } else if (preset === 'last') {
      setStartDate(moment().subtract(1, 'month').startOf('month').toDate());
      setEndDate(moment().subtract(1, 'month').endOf('month').toDate());
    } else if (preset === 'year') {
      setStartDate(moment().startOf('year').toDate());
      setEndDate(moment().endOf('year').toDate());
    } else if (preset === '30') {
      setStartDate(moment().subtract(29, 'days').startOf('day').toDate());
      setEndDate(moment().endOf('day').toDate());
    }
  };

  const money = useMemo(() => {
    const txs = transactions.filter((t) => inRange(t.datetime, startDate, endDate));
    const income = txs.filter((t) => t.incoming).reduce((s, t) => s + Number(t.amount), 0);
    const expense = txs.filter((t) => !t.incoming).reduce((s, t) => s + Number(t.amount), 0);

    const byCategory = {};
    txs.filter((t) => !t.incoming).forEach((t) => {
      const key = (t.category || 'other').replace(/_/g, ' ');
      byCategory[key] = (byCategory[key] || 0) + Number(t.amount);
    });
    const topCategories = Object.entries(byCategory)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6);

    const subPay = subscriptions
      .filter((s) => s.subscription_type !== 'incoming')
      .reduce((s, sub) => s + toMonthly(sub.amount, sub.frequency), 0);
    const subGet = subscriptions
      .filter((s) => s.subscription_type === 'incoming')
      .reduce((s, sub) => s + toMonthly(sub.amount, sub.frequency), 0);

    const balance = accounts.reduce((s, a) => s + Number(a.total_balance), 0);

    return {
      income,
      expense,
      net: income - expense,
      txCount: txs.length,
      topCategories,
      maxCat: topCategories[0]?.[1] || 0,
      subPay,
      subGet,
      balance,
      accounts,
    };
  }, [transactions, subscriptions, accounts, startDate, endDate]);

  const business = useMemo(() => {
    const openInv = invoices.filter((i) => i.status === 'open' || i.status === 'draft');
    const owedToMe = openInv
      .filter((i) => i.direction === 'outgoing')
      .reduce((s, i) => s + Number(i.amount) + Number(i.vat_amount || 0), 0);
    const iOweInv = openInv
      .filter((i) => i.direction === 'incoming')
      .reduce((s, i) => s + Number(i.amount) + Number(i.vat_amount || 0), 0);
    const overdueInv = invoices.filter((i) => i.is_overdue).length;
    const paidInRange = invoices.filter(
      (i) => i.status === 'paid' && i.paid_at && inRange(i.paid_at, startDate, endDate)
    );
    const paidTotal = paidInRange.reduce(
      (s, i) => s + Number(i.amount) + Number(i.vat_amount || 0),
      0
    );

    const openDebts = debts.filter((d) => d.status === 'open' || d.status === 'partial');
    const receivable = openDebts
      .filter((d) => d.direction === 'receivable')
      .reduce((s, d) => s + Number(d.remaining), 0);
    const payable = openDebts
      .filter((d) => d.direction === 'payable')
      .reduce((s, d) => s + Number(d.remaining), 0);

    return {
      owedToMe,
      iOweInv,
      overdueInv,
      paidTotal,
      paidCount: paidInRange.length,
      receivable,
      payable,
      openDebtCount: openDebts.length,
    };
  }, [invoices, debts, startDate, endDate]);

  const work = useMemo(() => {
    const openTasks = tasks.filter((t) => t.status !== 'done');
    const doneInRange = tasks.filter(
      (t) => t.status === 'done' && inRange(t.updated_at || t.created_at, startDate, endDate)
    );
    const overdue = openTasks.filter((t) => {
      if (!t.due_date) return false;
      return moment(t.due_date).isBefore(moment(), 'minute');
    });

    const byKind = { request: 0, appointment: 0, personal: 0 };
    openTasks.forEach((t) => {
      const k = t.kind || 'request';
      byKind[k] = (byKind[k] || 0) + 1;
    });

    const upcomingAppts = tasks
      .filter(
        (t) =>
          t.kind === 'appointment' &&
          t.status !== 'done' &&
          t.due_date &&
          moment(t.due_date).isSameOrAfter(moment(), 'day')
      )
      .sort((a, b) => moment(a.due_date).valueOf() - moment(b.due_date).valueOf())
      .slice(0, 6);

    const entries = timeEntries.filter((e) => inRange(e.date, startDate, endDate));
    const hours = entries.reduce((s, e) => s + Number(e.hours), 0);
    const billable = entries.reduce(
      (s, e) => s + Number(e.hours) * Number(e.cost_per_hour || 0),
      0
    );
    const byCompany = {};
    entries.forEach((e) => {
      const name = e.company_name || `Company #${e.company}`;
      if (!byCompany[name]) byCompany[name] = { hours: 0, value: 0 };
      byCompany[name].hours += Number(e.hours);
      byCompany[name].value += Number(e.hours) * Number(e.cost_per_hour || 0);
    });
    const companyRows = Object.entries(byCompany)
      .map(([name, v]) => ({ name, ...v }))
      .sort((a, b) => b.hours - a.hours)
      .slice(0, 8);

    const myHours = entries
      .filter((e) => {
        const p = (e.person || 'Me').trim().toLowerCase();
        return p === 'me' || p === 'εγώ' || p === 'εγω' || p === '';
      })
      .reduce((s, e) => s + Number(e.hours), 0);

    return {
      openCount: openTasks.length,
      doneCount: doneInRange.length,
      overdueCount: overdue.length,
      byKind,
      upcomingAppts,
      overdue: overdue.slice(0, 6),
      hours,
      myHours,
      billable,
      companyRows,
    };
  }, [tasks, timeEntries, startDate, endDate]);

  const wishOpen = useMemo(() => {
    const open = wishlist.filter((w) => !w.is_purchased);
    return {
      count: open.length,
      cost: open.reduce((s, w) => s + (Number(w.est_cost) || 0), 0),
    };
  }, [wishlist]);

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <div className="d-flex justify-content-between align-items-start mb-4 flex-wrap gap-3">
        <div>
          <h2 style={{ color: ACCENT, marginBottom: 4 }}>Reporting</h2>
          <div style={{ color: '#9ca3af', fontSize: 13 }}>
            Money · invoices · hours · tasks — one snapshot
          </div>
        </div>
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <Button size="sm" variant="outline-secondary" onClick={() => setPreset('30')}>
            30 days
          </Button>
          <Button size="sm" variant="outline-secondary" onClick={() => setPreset('month')}>
            This month
          </Button>
          <Button size="sm" variant="outline-secondary" onClick={() => setPreset('last')}>
            Last month
          </Button>
          <Button size="sm" variant="outline-secondary" onClick={() => setPreset('year')}>
            This year
          </Button>
          <div className="d-flex align-items-center gap-2">
            <DatePicker
              selected={startDate}
              onChange={setStartDate}
              dateFormat="dd/MM/yyyy"
              className="form-control form-control-sm bg-dark text-light border-secondary"
              maxDate={endDate}
            />
            <span style={{ color: '#9ca3af' }}>→</span>
            <DatePicker
              selected={endDate}
              onChange={setEndDate}
              dateFormat="dd/MM/yyyy"
              className="form-control form-control-sm bg-dark text-light border-secondary"
              minDate={startDate}
            />
          </div>
        </div>
      </div>

      <Row className="g-3 mb-3">
        <Col md={3} sm={6}>
          <StatCard
            icon={<FaWallet />}
            label="Net cashflow"
            value={euro(money.net)}
            sub={`${money.txCount} transactions in range`}
            color={money.net >= 0 ? '#22c55e' : '#f87171'}
          />
        </Col>
        <Col md={3} sm={6}>
          <StatCard
            icon={<FaFileInvoice />}
            label="Clients owe you"
            value={euro(business.owedToMe)}
            sub={business.overdueInv ? `${business.overdueInv} overdue invoices` : 'Open outgoing invoices'}
            color="#22c55e"
          />
        </Col>
        <Col md={3} sm={6}>
          <StatCard
            icon={<FaClock />}
            label="Hours logged"
            value={work.hours.toFixed(1)}
            sub={`${euro(work.billable)} · you ${work.myHours.toFixed(1)}h`}
          />
        </Col>
        <Col md={3} sm={6}>
          <StatCard
            icon={<FaTasks />}
            label="Open tasks"
            value={String(work.openCount)}
            sub={
              work.overdueCount
                ? `${work.overdueCount} overdue · ${work.doneCount} done in range`
                : `${work.doneCount} done in range`
            }
            color={work.overdueCount ? '#f87171' : ACCENT}
          />
        </Col>
      </Row>

      <Row className="g-3">
        <Col lg={6}>
          <Section
            title="Money"
            action={
              <Link to="/transactions" style={{ color: ACCENT, fontSize: 13 }}>
                Open Money →
              </Link>
            }
          >
            <Row className="mb-3">
              <Col xs={4}>
                <div style={{ color: '#9ca3af', fontSize: 12 }}>Income</div>
                <div className="text-success fw-bold">{euro(money.income)}</div>
              </Col>
              <Col xs={4}>
                <div style={{ color: '#9ca3af', fontSize: 12 }}>Expenses</div>
                <div className="text-danger fw-bold">{euro(money.expense)}</div>
              </Col>
              <Col xs={4}>
                <div style={{ color: '#9ca3af', fontSize: 12 }}>Accounts</div>
                <div style={{ color: '#fbbf24', fontWeight: 700 }}>{euro(money.balance)}</div>
              </Col>
            </Row>

            <div style={{ color: '#9ca3af', fontSize: 13, marginBottom: 8 }}>
              Top expense categories
            </div>
            {money.topCategories.length === 0 ? (
              <div style={{ color: '#9ca3af' }}>No spending in this range.</div>
            ) : (
              money.topCategories.map(([label, value]) => (
                <BarRow key={label} label={label} value={value} max={money.maxCat} color="#f87171" />
              ))
            )}

            <hr className="border-secondary" />
            <div className="d-flex justify-content-between small" style={{ color: '#d1d5db' }}>
              <span>Recurring pay / month</span>
              <span className="text-danger">{euro(money.subPay)}</span>
            </div>
            <div className="d-flex justify-content-between small" style={{ color: '#d1d5db' }}>
              <span>Recurring get / month</span>
              <span className="text-success">{euro(money.subGet)}</span>
            </div>
          </Section>

          <Section
            title="Invoices & debts"
            action={
              <Link to="/invoices" style={{ color: ACCENT, fontSize: 13 }}>
                Invoices →
              </Link>
            }
          >
            <Row className="mb-3">
              <Col xs={6}>
                <div style={{ color: '#9ca3af', fontSize: 12 }}>Open invoices (out)</div>
                <div className="text-success fw-bold">{euro(business.owedToMe)}</div>
              </Col>
              <Col xs={6}>
                <div style={{ color: '#9ca3af', fontSize: 12 }}>Open invoices (in)</div>
                <div className="text-danger fw-bold">{euro(business.iOweInv)}</div>
              </Col>
            </Row>
            <div className="d-flex justify-content-between small mb-2" style={{ color: '#d1d5db' }}>
              <span>Paid in range</span>
              <span>
                {business.paidCount} · {euro(business.paidTotal)}
              </span>
            </div>
            <hr className="border-secondary" />
            <div className="d-flex align-items-center gap-2 mb-2" style={{ color: '#9ca3af', fontSize: 13 }}>
              <FaHandshake /> IOUs
              <Link to="/transactions" style={{ color: ACCENT, marginLeft: 'auto', fontSize: 12 }}>
                Debts tab →
              </Link>
            </div>
            <div className="d-flex justify-content-between">
              <div>
                <div style={{ color: '#9ca3af', fontSize: 12 }}>They owe me</div>
                <div className="text-success fw-bold">{euro(business.receivable)}</div>
              </div>
              <div className="text-end">
                <div style={{ color: '#9ca3af', fontSize: 12 }}>I owe</div>
                <div className="text-danger fw-bold">{euro(business.payable)}</div>
              </div>
            </div>
          </Section>
        </Col>

        <Col lg={6}>
          <Section
            title="Work & appointments"
            action={
              <Link to="/tasks" style={{ color: ACCENT, fontSize: 13 }}>
                Tasks →
              </Link>
            }
          >
            <div className="d-flex flex-wrap gap-2 mb-3">
              <Badge bg="secondary">Requests {work.byKind.request || 0}</Badge>
              <Badge bg="info">Appointments {work.byKind.appointment || 0}</Badge>
              <Badge bg="warning" text="dark">Personal {work.byKind.personal || 0}</Badge>
              {work.overdueCount > 0 && (
                <Badge bg="danger">Overdue {work.overdueCount}</Badge>
              )}
            </div>

            <div style={{ color: '#9ca3af', fontSize: 13, marginBottom: 8 }}>
              <FaCalendarAlt className="me-1" /> Upcoming appointments
            </div>
            {work.upcomingAppts.length === 0 ? (
              <div className="mb-3" style={{ color: '#9ca3af' }}>None scheduled.</div>
            ) : (
              <Table size="sm" variant="dark" className="mb-3">
                <tbody>
                  {work.upcomingAppts.map((t) => (
                    <tr key={t.id}>
                      <td>
                        <Link to={`/task/${t.id}`} style={{ color: ACCENT, textDecoration: 'none' }}>
                          {t.title}
                        </Link>
                        {t.with_person || t.location ? (
                          <div>
                            <small style={{ color: '#9ca3af' }}>
                              {[t.with_person, t.location].filter(Boolean).join(' · ')}
                            </small>
                          </div>
                        ) : null}
                      </td>
                      <td className="text-end" style={{ color: '#d1d5db', whiteSpace: 'nowrap' }}>
                        {moment(t.due_date).format('DD MMM HH:mm')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}

            {work.overdue.length > 0 && (
              <>
                <div style={{ color: '#f87171', fontSize: 13, marginBottom: 8 }}>Overdue</div>
                <Table size="sm" variant="dark" className="mb-0">
                  <tbody>
                    {work.overdue.map((t) => (
                      <tr key={t.id}>
                        <td>
                          <Link to={`/task/${t.id}`} style={{ color: '#f87171', textDecoration: 'none' }}>
                            {t.title}
                          </Link>
                          <Badge bg="secondary" className="ms-2" style={{ fontSize: 10 }}>
                            {t.kind}
                          </Badge>
                        </td>
                        <td className="text-end" style={{ color: '#9ca3af', whiteSpace: 'nowrap' }}>
                          {t.due_date ? moment(t.due_date).format('DD MMM') : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}
          </Section>

          <Section
            title="Time by company"
            action={
              <Link to="/time-tracking" style={{ color: ACCENT, fontSize: 13 }}>
                Time →
              </Link>
            }
          >
            {work.companyRows.length === 0 ? (
              <div style={{ color: '#9ca3af' }}>No hours in this range.</div>
            ) : (
              <Table size="sm" hover variant="dark" className="mb-0 align-middle">
                <thead>
                  <tr>
                    <th>Company</th>
                    <th className="text-end">Hours</th>
                    <th className="text-end">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {work.companyRows.map((row) => (
                    <tr key={row.name}>
                      <td>{row.name}</td>
                      <td className="text-end">{row.hours.toFixed(1)}</td>
                      <td className="text-end" style={{ color: ACCENT }}>{euro(row.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Section>

          {wishOpen.count > 0 && (
            <Card className="bg-dark text-light border-secondary mb-3">
              <Card.Body className="d-flex justify-content-between align-items-center py-3">
                <div>
                  <div style={{ color: '#9ca3af', fontSize: 13 }}>Wishlist still open</div>
                  <div style={{ fontWeight: 600 }}>
                    {wishOpen.count} items · {euro(wishOpen.cost)}
                  </div>
                </div>
                <Link to="/wishlist" style={{ color: ACCENT, fontSize: 13 }}>
                  Wishlist →
                </Link>
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default Reporting;
